import { DeleteOutlined, InfoCircleTwoTone } from "@ant-design/icons";
import { AppPaths } from "@p0security/shared/routes/constants";
import {
  Alert,
  Button,
  Divider,
  Flex,
  InputRef,
  Menu,
  MenuRef,
  Modal,
  Popover,
  Select,
  Typography,
  message,
} from "antd";
import type {
  ItemType,
  MenuItemGroupType,
  MenuItemType,
} from "antd/es/menu/interface";
import { DefaultOptionType } from "antd/es/select";
import Link from "antd/lib/typography/Link";
import { LinkWithEnvironment } from "components/Assessment/components/LinkWithEnvironment";
import { SettingsPaths } from "components/Assessment/components/Settings/paths";
import { useMonitors } from "components/Assessment/contexts/MonitorsContext";
import { ScopeContext } from "components/Assessment/contexts/ScopeContext";
import { GraphTooltip } from "components/GraphTooltip";
import { TooltipCode } from "components/TooltipCode";
import { SearchInput } from "components/antd";
import { SpaceBetweenDiv, VerticalDiv } from "components/divs";
import { DOCS_URLS } from "constants/docsUrls";
import { Searcher as FuzzySearcher } from "fast-fuzzy";
import { useLocalStorage } from "hooks/useLocalStorage";
import { compact } from "lodash";
import React, {
  ChangeEvent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { v4 as uuid } from "uuid";

import { CreateMonitorButton } from "./Buttons/CreateMonitor";
import { InteractiveTermBuilder } from "./InteractiveTermBuilder";
import { Prefix } from "./Prefix";

// Styled suggestion menu to adjust item padding
const SuggestionMenu = styled(Menu)`
  max-height: 50vh;
  overflow-y: auto;
  li.ant-menu-item {
    height: unset;
    line-height: 18px;
    margin: 0;
    padding: 4px;
    width: unset;
    .delete-saved-btn {
      visibility: hidden;
    }
  }
  li.ant-menu-item.ant-menu-item-active .delete-saved-btn {
    visibility: visible;
  }
`;

const MAX_SEARCH_HISTORY = 1000;
const WHITESPACE = /\s+/;

const { confirm } = Modal;

type SuggestionItem = {
  /** Identifier for saved items */
  id?: string;
  /** Internal search term key */
  key: string;
  /** Displayed label */
  label: ReactNode;
  /** Source type of the suggestion */
  source: "history" | "monitor" | "saved";
};

export interface GraphSearchProps {
  controls: {
    display: string;
    terms: string;
    show: string;
    onChangeDisplay: (value: string) => void;
    onChangeTerms: (value: string) => void;
    onChangeShow: (value: string) => void;
  };
  frozen?: {
    display?: boolean;
    terms?: string;
    show?: string;
    help?: ReactNode;
  };
  isSearching: boolean;
  extra?: { search?: boolean; show?: ReactNode; toggle?: ReactNode };
  scopeNode?: ReactNode;
  showOptions: DefaultOptionType[];
}

export const GraphSearch: React.FC<GraphSearchProps> = ({
  controls,
  frozen,
  isSearching,
  extra,
  scopeNode,
  showOptions,
}) => {
  const [searchHistory, setSearchHistory] = useLocalStorage<
    Record<string, string[]>
  >("graph-search-history", {});
  const [savedSearches, setSavedSearches] = useLocalStorage<
    Record<string, any>
  >("graph-saved-searches", {});

  const { integration } = useContext(ScopeContext);
  const { monitors } = useMonitors();

  const deleteSearch = useCallback(
    (id: string, show: string) => {
      setSavedSearches((prev) => ({
        ...prev,
        [show]: prev[show].filter((s: any) => s.id !== id),
      }));
    },
    [setSavedSearches]
  );

  const savedItems: SuggestionItem[] = (savedSearches[controls.show] ?? []).map(
    (s: any) => ({
      id: s.id,
      key: s.searchTerm,
      label: s.name,
      source: "saved",
    })
  );

  const deleteHistory = useCallback(
    (term: string) => {
      setSearchHistory((prev) => {
        const arr = prev[controls.show] ?? [];
        return { ...prev, [controls.show]: arr.filter((t) => t !== term) };
      });
    },
    [controls.show, setSearchHistory]
  );

  const addSearch = useCallback(
    (name: string, searchTerm: string, show: string) => {
      const newSearch: any = {
        id: uuid(),
        name,
        searchTerm,
      };
      setSavedSearches((prev) => ({
        ...prev,
        [show]: [...(prev[show] ?? []), newSearch],
      }));
      deleteHistory(searchTerm);
      message.success("Search saved");
    },
    [setSavedSearches, deleteHistory]
  );

  /** Monitors matching current `show` and scope */
  const monitorItems = useMemo<SuggestionItem[]>(
    () =>
      Object.values(monitors ?? {})
        .filter(
          (m) => m.show === controls.show && m.scopes.includes(integration)
        )
        .map((m) => ({
          key: m.searchTerm,
          label: m.label,
          source: "monitor",
        })),
    [controls.show, monitors, integration]
  );

  const addToHistory = useCallback(
    (term: string) => {
      setSearchHistory((prev) => {
        const arr = [...(prev[controls.show] ?? []), term];
        const sliced = arr.slice(-MAX_SEARCH_HISTORY);
        return { ...prev, [controls.show]: sliced };
      });
    },
    [controls.show, setSearchHistory]
  );

  return (
    <GraphSearchInput
      addSearch={addSearch}
      addToHistory={addToHistory}
      controls={controls}
      deleteHistory={deleteHistory}
      deleteSaved={deleteSearch}
      extra={extra}
      frozen={frozen}
      historyItems={searchHistory}
      isSearching={isSearching}
      monitorItems={monitorItems}
      savedItems={savedItems}
      scopeNode={scopeNode}
      showOptions={showOptions}
    />
  );
};

const GraphSearchHelp: React.FC<object> = () => (
  <div>
    <p>Anything you type here will filter displayed data:</p>
    <ul>
      <li>
        <TooltipCode>keyword</TooltipCode>&nbsp;&minus;&nbsp;Show data
        containing <TooltipCode>keyword</TooltipCode>.
      </li>
      <li>
        <TooltipCode>column:</TooltipCode>&nbsp;&minus;&nbsp;Show data where{" "}
        <TooltipCode>column</TooltipCode> has content.
      </li>
      <li>
        <TooltipCode>column:keyword</TooltipCode>
        &nbsp;&minus;&nbsp;Show data data where{" "}
        <TooltipCode>column</TooltipCode> is linked to{" "}
        <TooltipCode>keyword</TooltipCode>.
      </li>
      <li>
        <TooltipCode>column=keyword</TooltipCode>
        &nbsp;&minus;&nbsp;Show data data where{" "}
        <TooltipCode>column</TooltipCode> contains{" "}
        <TooltipCode>keyword</TooltipCode>.
      </li>
      <li>
        <TooltipCode>!keyword</TooltipCode>&nbsp;&minus;&nbsp;Show data with at
        least one value not matching <TooltipCode>keyword</TooltipCode>.
      </li>
      <li>
        <TooltipCode>column:!keyword</TooltipCode>&nbsp;&minus;&nbsp;Show data
        where <TooltipCode>column</TooltipCode> is linked to at least one value
        not matching <TooltipCode>keyword</TooltipCode>.
      </li>
      <li>
        <TooltipCode>^term</TooltipCode>&nbsp;&minus;&nbsp;Removes items
        matching the <TooltipCode>term</TooltipCode> query from search results.
      </li>
    </ul>
    <p>
      For detailed help, see the{" "}
      {/* Deliberately keep referrer here; we'll use it for analytics */}
      <Link
        href={DOCS_URLS.IAM_ASSESSMENT_QUERY_SEARCH}
        rel="noopener"
        target="docs"
      >
        query-search reference
      </Link>
      .
    </p>
  </div>
);

interface GraphSearchInputProps {
  controls: GraphSearchProps["controls"];
  frozen?: GraphSearchProps["frozen"];
  isSearching: boolean;
  extra?: GraphSearchProps["extra"];
  scopeNode?: ReactNode;
  showOptions: DefaultOptionType[];
  historyItems: Record<string, string[]>;
  addToHistory: (term: string) => void;
  deleteHistory: (term: string) => void;
  savedItems: SuggestionItem[];
  deleteSaved: (id: string, show: string) => void;
  monitorItems: SuggestionItem[];
  addSearch: (name: string, searchTerm: string, show: string) => void;
}

const GraphSearchInput: React.FC<GraphSearchInputProps> = ({
  controls,
  frozen,
  isSearching,
  extra,
  scopeNode,
  showOptions,
  historyItems,
  addToHistory,
  deleteHistory,
  savedItems,
  deleteSaved,
  monitorItems,
  addSearch,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [staged, setStaged] = useState<string>();
  const [suppressSuggestions, setSuppressSuggestions] = useState(true);
  const [isTermBuilderOpen, setIsTermBuilderOpen] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showCreateMonitorHint, setShowCreateMonitorHint] = useState(false);
  const ignoreNextFocus = useRef(false);

  const whereRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<InputRef>(null);
  const suggestionsRef = useRef<MenuRef>(null);
  const [menuWidth, setMenuWidth] = useState<number>(0);

  // Helper to recalculate and store the current width of the <div> wrapping the input
  const recalcWidth = useCallback(() => {
    const w = whereRef.current?.offsetWidth ?? 0;
    setMenuWidth(w);
  }, []);

  // On mount + whenever the window resizes, recalc
  useEffect(() => {
    recalcWidth();
    window.addEventListener("resize", recalcWidth);
    return () => {
      window.removeEventListener("resize", recalcWidth);
    };
  }, [recalcWidth]);

  const getMenuItems = useCallback((): HTMLElement[] => {
    const list = suggestionsRef.current?.menu?.list;
    if (!list) return [];
    return Array.from(list.querySelectorAll('li[role="menuitem"]'));
  }, []);

  const closeSuggestions = useCallback((opts?: { fromMenu?: boolean }) => {
    setSuppressSuggestions(true);
    // prevent onFocus from re-enabling suggestions
    ignoreNextFocus.current = !!opts?.fromMenu;
    if (opts?.fromMenu) searchRef.current?.focus();
  }, []);

  const handleShowChange = useCallback(
    (value: string) => {
      setShowLoading(true);
      controls.onChangeShow(value);
    },
    [controls]
  );

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setStaged(e.target.value);
    setSuppressSuggestions(false);
  }, []);

  const handleCloseTermBuilder = useCallback(() => {
    setIsTermBuilderOpen(false);
  }, []);

  const handleOpenTermBuilder = useCallback(() => {
    setIsTermBuilderOpen(true);
  }, []);

  const handleInputFocus = useCallback(() => {
    if (ignoreNextFocus.current) {
      ignoreNextFocus.current = false;
      return;
    }
    setSuppressSuggestions(false);
  }, []);

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const items = getMenuItems();
      switch (e.key) {
        case "Escape":
          if (!suppressSuggestions && items.length) {
            e.preventDefault();
            closeSuggestions({ fromMenu: true });
          }
          break;
        case "ArrowDown":
          if (items.length) {
            e.preventDefault();
            items[0].focus();
          }
          break;
        case "ArrowUp":
          if (items.length) {
            e.preventDefault();
            items[items.length - 1].focus();
          }
          break;
      }
    },
    [getMenuItems, suppressSuggestions, closeSuggestions]
  );

  const handleItemKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLLIElement>) => {
      const items = getMenuItems();
      const idx = items.indexOf(e.currentTarget as HTMLElement);
      e.preventDefault();

      switch (e.key) {
        case "Escape":
          closeSuggestions({ fromMenu: true });
          break;
        case "ArrowDown":
          if (idx < items.length - 1) {
            items[idx + 1].focus();
          } else {
            e.stopPropagation();
            searchRef.current?.focus();
          }
          break;
        case "ArrowUp":
          if (idx > 0) {
            items[idx - 1].focus();
          } else {
            e.stopPropagation();
            searchRef.current?.focus();
          }
          break;
      }
    },
    [getMenuItems, closeSuggestions]
  );

  const handleAddInteractive = useCallback(
    (term: string) => {
      setStaged((prev) => {
        const base = prev ?? controls.terms;
        return base.trim() ? `${base} ${term}`.trim() : term;
      });
      setSuppressSuggestions(true);
    },
    [controls.terms]
  );

  const updateTerms = useCallback(() => {
    if (staged !== undefined) {
      const term = staged.trim();
      if (term.length > 0) {
        addToHistory(term);
      }
      controls.onChangeTerms(term);
      closeSuggestions({ fromMenu: true });
    }
  }, [staged, addToHistory, controls, closeSuggestions]);

  const handleSuggestionClick = useCallback(
    ({ key }: { key: string }) => {
      const term = key.replace(/^(monitor|history|saved)-/, "");
      setStaged(undefined);
      controls.onChangeTerms(term);
    },
    [controls]
  );

  const handleDeleteHistoryClick = useCallback(
    (term: string) => deleteHistory(term),
    [deleteHistory]
  );

  const handleConfirmDeleteSavedSearch = useCallback(
    (id: string) => {
      confirm({
        title: "Delete saved search?",
        okButtonProps: { danger: true },
        onOk: () => deleteSaved(id, controls.show),
      });
    },
    [deleteSaved, controls.show]
  );

  const suggestionSources = useMemo(() => {
    const history = (historyItems[controls.show] ?? []).map((t) => ({
      key: t,
      label: t,
      source: "history" as const,
    }));
    return [...history, ...monitorItems, ...savedItems];
  }, [historyItems, controls.show, monitorItems, savedItems]);

  // Fuzzy match suggestions
  // We want to fuzzy search on recent searches and monitors,
  // but display all saved searches
  const suggestions = useMemo(() => {
    const words = compact(
      (staged ?? "").split(WHITESPACE).map((w) => w.trim())
    );
    if (!words.length) return [];

    const searcher = new FuzzySearcher(suggestionSources, {
      keySelector: (s) => String(s.label),
      threshold: 0.5,
    });
    const map = new Map<string, SuggestionItem & { count: number }>();

    // Add all saved searches first
    for (const item of savedItems) {
      const uid = `${item.source}-${item.key}`;
      map.set(uid, { ...item, count: 1 });
    }

    // Then add fuzzy matches for other items
    for (const w of words) {
      for (const match of searcher.search(w)) {
        // Skip saved items as they're already added
        if (match.source === "saved") continue;

        const uid = `${match.source}-${match.key}`;
        map.set(uid, { ...match, count: (map.get(uid)?.count ?? 0) + 1 });
      }
    }

    // Sort and slice non-saved items
    const allItems = Array.from(map.values());
    const savedSearchResults = allItems.filter(
      (item) => item.source === "saved"
    );
    const monitorResults = allItems
      .filter((item) => item.source === "monitor")
      .slice(0, 10);

    const historyResults = allItems
      .filter((item) => item.source === "history")
      .slice(0, 5);

    const otherItems = [...monitorResults, ...historyResults].sort((a, b) => {
      const countDiff = b.count - a.count;
      if (countDiff !== 0) return countDiff;
      return String(a.label).length - String(b.label).length;
    });

    return [...savedSearchResults, ...otherItems];
  }, [staged, suggestionSources, savedItems]);

  const menuItems = useMemo<ItemType<MenuItemType>[]>(() => {
    if (!suggestions.length) {
      return [
        {
          key: "empty",
          label: (
            <Typography.Text style={{ color: "#888" }}>
              No suggestions
            </Typography.Text>
          ),
          disabled: true,
        },
      ];
    }

    const section = (
      label: React.ReactNode,
      items: SuggestionItem[],
      render: (s: SuggestionItem) => React.ReactNode
    ): ItemType<MenuItemType> => ({
      type: "group",
      label,
      children: items.map((s) => ({
        key: `${s.source}-${s.key}`,
        label: render(s),
        onKeyDown: handleItemKeyDown,
      })),
    });

    const handleDeleteHistory = (s: SuggestionItem) => {
      handleDeleteHistoryClick(s.key);
    };

    const handleDeleteSaved = (s: SuggestionItem) => {
      if (s.id) {
        handleConfirmDeleteSavedSearch(s.id);
      }
    };

    return [
      section(
        <>
          <span>Top 5 History Matches</span>
          <Divider style={{ margin: 0 }} />
        </>,
        suggestions.filter((s) => s.source === "history"),
        (s) => (
          <Flex justify="space-between">
            <span>{s.label}</span>
            <Button
              className="delete-saved-btn"
              size="small"
              type="link"
              // already memoized
              // eslint-disable-next-line react/jsx-no-bind
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteHistory(s);
              }}
            >
              <DeleteOutlined />
            </Button>
          </Flex>
        )
      ),
      section(
        <>
          <span>Saved Searches</span>
          <Divider style={{ margin: 0 }} />
        </>,
        suggestions.filter((s) => s.source === "saved"),
        (s) => (
          <Flex justify="space-between" align="center">
            <VerticalDiv>
              <span>{s.label}</span>
              {s.key && (
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: "0.85em" }}
                  ellipsis
                >
                  {s.key}
                </Typography.Text>
              )}
            </VerticalDiv>
            <Button
              className="delete-saved-btn"
              size="small"
              type="link"
              // already memoized
              // eslint-disable-next-line react/jsx-no-bind
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteSaved(s);
              }}
            >
              <DeleteOutlined />
            </Button>
          </Flex>
        )
      ),
      section(
        <>
          <Flex justify="space-between" align="center">
            <span>Top Monitor Matches</span>
            <LinkWithEnvironment
              to={`../${AppPaths.Settings}/${SettingsPaths.Monitors}`}
            >
              View All
            </LinkWithEnvironment>
          </Flex>
          <Divider style={{ margin: 0 }} />
        </>,
        suggestions.filter((s) => s.source === "monitor"),
        (s) => (
          <VerticalDiv>
            <span>{s.label}</span>
            {s.key && (
              <Typography.Text
                style={{ fontSize: "0.85em" }}
                type="secondary"
                ellipsis
              >
                {s.key}
              </Typography.Text>
            )}
          </VerticalDiv>
        )
      ),
    ];
  }, [
    suggestions,
    handleItemKeyDown,
    handleDeleteHistoryClick,
    handleConfirmDeleteSavedSearch,
  ]);

  useEffect(() => setShowLoading(false), [controls.show]);
  useEffect(() => setStaged(undefined), [controls.terms]);

  useEffect(() => {
    if (searchParams.get("createMonitor") === "true") {
      setShowCreateMonitorHint(true);
      searchParams.delete("createMonitor");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <div style={{ display: "grid", gap: "0.5em", flexGrow: 1 }}>
      {/* Show hint for creating monitors */}
      {showCreateMonitorHint && (
        <Alert
          message="To create a new monitor, enter a search query below and click the 'Save Search' button"
          type="info"
          showIcon
          closable
          style={{ marginBottom: "1em" }}
        />
      )}
      {/* Show select & ancillary toggles */}
      <SpaceBetweenDiv style={{ alignItems: "center", width: "100%" }}>
        <Flex gap="1em" align="center">
          <Flex align="baseline" gap="1em">
            <Prefix prefix="Show" textWidth="md">
              <Select
                style={{ minWidth: "9em" }}
                disabled={!!frozen?.show}
                options={showOptions}
                value={controls.show}
                loading={showLoading}
                onChange={handleShowChange}
              />
            </Prefix>
            {scopeNode && <Prefix prefix="in">{scopeNode}</Prefix>}
          </Flex>
          {extra?.toggle && (
            <Flex align="baseline" gap="1em">
              {extra.toggle}
            </Flex>
          )}
        </Flex>
        {extra?.show && (
          <Flex align="baseline" gap="1em">
            {extra.show}
          </Flex>
        )}
      </SpaceBetweenDiv>

      <SpaceBetweenDiv
        style={{
          alignItems: "center",
          display: "inline-flex",
          flexWrap: "nowrap",
          width: "100%",
        }}
      >
        <Prefix
          prefix={
            <Flex align="center">
              Where&nbsp;
              <GraphTooltip title={<GraphSearchHelp />} width="500px">
                <InfoCircleTwoTone />
              </GraphTooltip>
            </Flex>
          }
          textWidth="md"
        >
          <Popover
            data-testid="graph-search-suggestion-popover"
            placement="bottomLeft"
            arrow={false}
            content={
              <SuggestionMenu
                style={{ width: menuWidth }}
                items={menuItems.filter(
                  (m) => (m as MenuItemGroupType<MenuItemType>).children?.length
                )}
                onClick={handleSuggestionClick}
                ref={suggestionsRef}
              />
            }
            open={
              !!suggestions.length && !suppressSuggestions && !isTermBuilderOpen
            }
            styles={{
              body: { padding: 0, width: "100%" },
              root: { padding: 0 },
            }}
          >
            <Flex style={{ width: "100%" }} ref={whereRef}>
              <SearchInput
                data-testid="graph-search-input"
                enterButton={staged !== undefined}
                enterKeyHint="search"
                loading={isSearching}
                value={staged ?? controls.terms}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                onSearch={updateTerms}
                onPressEnter={updateTerms}
                onFocus={handleInputFocus}
                placeholder={
                  frozen?.terms
                    ? undefined
                    : "Enter search terms or click +, then press Enter"
                }
                ref={searchRef}
              />
              <InteractiveTermBuilder
                controls={controls}
                isOpen={isTermBuilderOpen}
                onOpen={handleOpenTermBuilder}
                onClose={handleCloseTermBuilder}
                onAddTerm={handleAddInteractive}
              />
              {extra?.search && (
                <Flex
                  align="center"
                  style={{
                    marginLeft: ".5em",
                  }}
                >
                  <CreateMonitorButton
                    disabled={controls.terms === staged}
                    addSearch={addSearch}
                    showTemplateOptions={false}
                  />
                </Flex>
              )}
            </Flex>
          </Popover>
        </Prefix>
      </SpaceBetweenDiv>
    </div>
  );
};
